/**
 * Decisions the two pay-in-full surfaces share about the account's shared Multibanco
 * code. Kept out of the components so both apply the same rules and can be tested
 * without rendering a page.
 */

/** Whether pay-in-full should serve the shared account code instead of a fresh mint. */
export function shouldServeSharedReference(input: {
  paymentMethod: string;
  isFullPayment: boolean;
  isScheduled: boolean;
  hasDiscount: boolean;
  coversWholeAccountBalance: boolean;
}): boolean {
  // A discount prices the payment below the balance the code is bound to, and a future
  // date cannot be honoured by an ATM code, so both keep the legacy per-checkout mint.
  // coversWholeAccountBalance rules out quoting an account-wide amount on a page that
  // showed one debt of several.
  return (
    input.paymentMethod === 'multibanco' &&
    input.isFullPayment &&
    !input.isScheduled &&
    !input.hasDiscount &&
    input.coversWholeAccountBalance
  );
}

/**
 * Locale key for a no-mint outcome from the engine.
 *
 * Only `payoff` / `no_active_balance` mean nothing is owed. `in_arrangement`,
 * `balance_changed` and `past_handback` all describe an outstanding balance, so none may
 * render "nothing outstanding to pay" — that tells someone who owes money they are settled.
 *
 * `past_handback` and `handback_too_soon` are terminal: the handback date does not move, so
 * their copy must not offer a retry the way `balance_changed` does. Keep this in step with
 * `_NO_MINT_MESSAGES` in `services/connect_api/services/payment_service.py` — the two
 * describe the same states.
 *
 * The default is the dangerous branch — it says nothing is outstanding — so every reason
 * that describes money still owed must be listed here explicitly, including ones the portal
 * cannot currently receive. `handback_too_soon` is one: it only fires on a channel with a
 * non-zero freshness floor (see `MULTIBANCO_FRESHNESS_FLOOR_DAYS` in
 * `forest/config/payments.py`), and the portal's is zero.
 */
export type NoMintReason =
  | 'payoff'
  | 'no_active_balance'
  | 'in_arrangement'
  | 'balance_changed'
  | 'past_handback'
  | 'handback_too_soon'
  | 'not_payable'
  | 'exceeds_balance'
  | 'channel_window_too_short'
  | 'below_minimum'
  | 'minimum_above_balance';

const NO_MINT_KEYS: Record<NoMintReason, string> = {
  payoff: 'payment.nothingOutstanding',
  no_active_balance: 'payment.nothingOutstanding',
  in_arrangement: 'payment.inArrangement',
  balance_changed: 'payment.balanceChanged',
  past_handback: 'payment.cannotIssueReference',
  handback_too_soon: 'payment.cannotIssueReference',
  not_payable: 'payment.debtNotPayableByReference',
  // Also returned when the amount is under the balance but above what a live discount or
  // account credit currently makes clearing this debt cost — the copy names the debt rather
  // than either figure, so it stays correct for both reasons.
  exceeds_balance: 'payment.amountExceedsDebt',
  channel_window_too_short: 'payment.cannotIssueReference',
  below_minimum: 'payment.belowMinimumPartial',
  minimum_above_balance: 'payment.minimumAbovePartialCeiling',
};

/** The refusals whose copy names what it refuses. Only the case surfaces take these: on the
 *  account product the same reason covers every debt, and debt-scoped wording there would
 *  tell a customer one debt is affected when none of them can take a code. */
const DEBT_SCOPED: Partial<Record<NoMintReason, string>> = {
  past_handback: 'payment.cannotIssueReferenceForDebt',
  handback_too_soon: 'payment.cannotIssueReferenceForDebt',
  channel_window_too_short: 'payment.cannotIssueReferenceForDebt',
};

export function noMintCopyKey(
  reason: string | null | undefined,
  scope: 'account' | 'case' = 'account',
): string {
  const key = reason as NoMintReason;
  // Never `nothingOutstanding`: a reason added server-side and not yet mapped here would
  // otherwise tell a customer nothing is owed on a debt that is very much outstanding.
  const unknown = scope === 'case' ? 'payment.cannotIssueReferenceForDebt' : 'payment.cannotIssueReference';
  return (scope === 'case' ? DEBT_SCOPED[key] : undefined) ?? NO_MINT_KEYS[key] ?? unknown;
}

/**
 * What to do when the mint request fails.
 *
 * `fall-back-to-legacy` — 404: the feature is off for this client, or there is no
 * account context, so the per-checkout mint is the right answer.
 * `tell-customer-to-retry` — 409/429: contention or rate limiting, both self-clearing.
 * Falling through here would mint a second code for the same balance, and neither
 * could then be retracted.
 * `rethrow` — anything else is a fault, not a state.
 */
export function mintFailureAction(status: number | undefined): 'fall-back-to-legacy' | 'tell-customer-to-retry' | 'rethrow' {
  if (status === 404) return 'fall-back-to-legacy';
  if (status === 409 || status === 429) return 'tell-customer-to-retry';
  return 'rethrow';
}
